import React from "react";
import { X } from "lucide-react";
import "../styles/modal.css";
import { useSelector } from "react-redux";
import { RootState } from "../redux/slices/store";

interface RowDetailsModalProps {
  rowData: any;
  onClose: () => void;
  excludeFields?: string[];
  context?: "supplierParts" | "itemAssignedRacks" | "priceHistory" | "cityMaster" | "userrole" | "default" | "pendingPO";
  menuTitle?: string; 
}

const RowDetailsModal: React.FC<RowDetailsModalProps> = ({
  rowData,
  onClose,
  excludeFields = [],
  context = "default",
  menuTitle = "Details", 
}) => {
  const permissions = useSelector((state: RootState) => state.permissions);
  
  const getDisplayName = () => {
    const menuKey = menuTitle.toLowerCase().replace(/\s+/g, "");
    
    for (const key in permissions.menus) {
      if (key.toLowerCase() === menuKey) {
        return permissions.menus[key].name || menuTitle;
      }
    } 
    return menuTitle;
  };

  const formatNestedObject = (obj: any) => {
    if (!obj) return {};
    const { _id, createdAt, updatedAt, __v, ...rest } = obj;
    return rest;
  };

  const extractStateDetails = (stateObj: any) => {
    if (!stateObj) return "N/A";
    if (typeof stateObj === "string" && stateObj.includes("statName")) {
      try {
        const parsed = JSON.parse(stateObj);
        return parsed.statName || "N/A";
      } catch (e) {
        console.error("Error parsing state details:", e);
        return stateObj;
      }
    }
    if (typeof stateObj === "object") {
      return stateObj.statName || "N/A";
    }
    
    return stateObj;
  };

  const extractCountryDetails = (countryObj: any) => {
    if (!countryObj) return "N/A";
    if (typeof countryObj === "string" && countryObj.includes("countryName")) {
      try {
        const parsed = JSON.parse(countryObj);
        return parsed.countryName || "N/A";
      } catch (e) {
        console.error("Error parsing country details:", e);
        return countryObj;
      }
    }

    if (typeof countryObj === "object") {
      return countryObj.countryName || "N/A";
    }
    
    return countryObj;
  };

  const extractPartDetails = (partDetailsStr: string) => {
    try {
      const partDetails =
        typeof partDetailsStr === "string"
          ? JSON.parse(partDetailsStr)
          : partDetailsStr;

      const cleanedDetails: { [key: string]: any } = {};
      if (partDetails.internalPartNo) 
        cleanedDetails["internalPartNo"] = partDetails.internalPartNo;

      return cleanedDetails;
    } catch (e) {
      console.error("Error parsing part details:", e);
      return { error: "Unable to parse part details" };
    }
  };

  const extractPoDetails = (poDetailsStr: string) => {
    try {
      const poDetails =
        typeof poDetailsStr === "string"
          ? JSON.parse(poDetailsStr)
          : poDetailsStr;

      const { poNo, poDate } = poDetails;

      const cleanedDetails: { [key: string]: any } = {};

      if (poNo) cleanedDetails["poNo"] = poNo;
      if (poDate) cleanedDetails["poDate"] = poDate;

      return cleanedDetails;
    } catch (e) {
      console.error("Error parsing PO details:", e);
      return { error: "Unable to parse PO details" };
    }
  };

  const extractPartDetailsFromId = (partIdObj: any) => {
    if (!partIdObj) return {};
    if (typeof partIdObj !== "object") return {};

    const cleanedDetails: { [key: string]: any } = {};
    if (partIdObj.part && partIdObj.part.internalPartNo) {
      cleanedDetails["internalPartNo"] = partIdObj.part.internalPartNo;
    } else if (partIdObj.internalPartNo) {
      cleanedDetails["internalPartNo"] = partIdObj.internalPartNo;
    }

    return cleanedDetails;
  };

  const extractPoDetailsFromId = (poIdObj: any) => {
    if (!poIdObj) return {};
    if (typeof poIdObj !== "object") return {};

    const cleanedDetails: { [key: string]: any } = {};

    if (poIdObj.poNo) cleanedDetails["poNo"] = poIdObj.poNo;
    if (poIdObj.poDate) {
      const date = new Date(poIdObj.poDate);
      cleanedDetails["poDate"] = isNaN(date.getTime())
        ? poIdObj.poDate
        : date.toLocaleDateString("en-CA");
    }

    return cleanedDetails;
  };

  const extractSupplierDetails = (supplierObj: any) => {
    if (!supplierObj) return {};
    
    const cleanedDetails: { [key: string]: any } = {};
    if (typeof supplierObj === "string") {
      try {
        const supplier = JSON.parse(supplierObj);
        if (supplier.name) cleanedDetails["supplierName"] = supplier.name;
      } catch (e) {
        console.error("Error parsing supplier details:", e);
      }
    } else if (typeof supplierObj === "object") {
      if (supplierObj.name) cleanedDetails["supplierName"] = supplierObj.name;
    }
    
    return cleanedDetails;
  };

  const extractRackDetails = (rackId: any) => {
    if (!rackId) return {};
    if (typeof rackId !== "object") return {};

    const cleanedDetails: { [key: string]: any } = {};

    if (rackId.rackName) cleanedDetails["rackName"] = rackId.rackName;

    return cleanedDetails;
  };

  const formatRowDetails = (row: any) => {
    if (context === "pendingPO") {
      const formattedDetails: { [key: string]: any } = {};
      if (row.poNumber) {
        formattedDetails["PO Number"] = row.poNumber;
      }
      
      if (row.poDate) {
        const date = new Date(row.poDate);
        formattedDetails["PO Date"] = isNaN(date.getTime())
          ? row.poDate
          : date.toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            }).replace(/\//g, "-");
      }
      if (row.supplierDetails && typeof row.supplierDetails === "object") {
        if (row.supplierDetails.name) {
          formattedDetails["Supplier Name"] = row.supplierDetails.name;
        }
      }
      if (row.parts && typeof row.parts === "object") {
        if (row.parts.internalPartNo) {
          formattedDetails["Internal Part No"] = row.parts.internalPartNo;
        }
        if (row.parts.partName) {
          formattedDetails["Part Name"] = row.parts.partName;
        }
        if (row.parts.categoryId && row.parts.categoryId.name) {
          formattedDetails["Category"] = row.parts.categoryId.name;
        }
        if (row.parts.modelId && row.parts.modelId.name) {
          formattedDetails["Model"] = row.parts.modelId.name;
        }
        if (row.parts.uomId && row.parts.uomId.name) {
          formattedDetails["UOM"] = row.parts.uomId.name;
        }
      }
      if (row.qty !== undefined) {
        formattedDetails["PO Quantity"] = row.qty;
      }
      
      if (row.balanceQty !== undefined) {
        formattedDetails["Balance Quantity"] = row.balanceQty;
      }
      
      if (row.qty !== undefined && row.balanceQty !== undefined) {
        formattedDetails["Received Quantity"] = row.qty - row.balanceQty;
      }

      if (row.dueDate) {
        const date = new Date(row.dueDate);
        formattedDetails["Due Date"] = isNaN(date.getTime())
          ? row.dueDate
          : date.toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            }).replace(/\//g, "-");
      }
      
      return formattedDetails;
    }

    if (context === "cityMaster") {
      const formattedDetails: { [key: string]: any } = {};
    
      if (row.cityName) {
        formattedDetails["cityName"] = row.cityName;
      }
      
      if (row.stateId) {
        formattedDetails["stateName"] = extractStateDetails(row.stateId);
      }
      
      if (row.countryId) {
        formattedDetails["countryName"] = extractCountryDetails(row.countryId);
      }

      if (row.status) {
        formattedDetails["status"] = row.status;
      }
      
      return formattedDetails;
    }

    if (context === "userrole") {
      const formattedDetails: { [key: string]: any } = {};
      if (row.firstName) formattedDetails["firstName"] = row.firstName;
      if (row.lastName) formattedDetails["lastName"] = row.lastName;
      if (row.email) formattedDetails["email"] = row.email;
      if (row.contactNo) formattedDetails["contactNo"] = row.contactNo;
      
      return formattedDetails;
    }
  
    const alwaysExcludeFields = [
      "tagId", 
      "rackId", 
      "createdBy", 
      "roleId", 
      "Role Id", 
      "sgst", 
      "cgst", 
      "igst", 
      "gst",
      "gstTotal"
    ];
    const combinedExcludeFields = [...excludeFields, ...alwaysExcludeFields];
    
    const { _id, createdAt, updatedAt, __v, ...rest } = row;
    const formattedDetails: { [key: string]: any } = {};
    let uomAdded = false;
    
    if (row.qty && !combinedExcludeFields.includes("qty")) {
      formattedDetails["qty"] = row.qty;
    }
    
    if (row.date && !combinedExcludeFields.includes("date")) {
      formattedDetails["date"] = row.date;
    }
    
    if (row.unitPrice && !combinedExcludeFields.includes("unitPrice")) {
      formattedDetails["unitPrice"] = row.unitPrice;
    }
    
    if (row.outwardStatus && !combinedExcludeFields.includes("outwardStatus")) {
      formattedDetails["outwardStatus"] = row.outwardStatus;
    }
    
    if (row.balanceQty && !combinedExcludeFields.includes("balanceQty")) {
      formattedDetails["balanceQty"] = row.balanceQty;
    }
    
    if (row.category && !combinedExcludeFields.includes("category")) {
      formattedDetails["category"] = row.category;
    }
    
    if (row.agedDays !== undefined && !combinedExcludeFields.includes("agedDays")) {
      formattedDetails["agedDays"] = row.agedDays;
    }
    
    if (row.agedQuantity !== undefined && !combinedExcludeFields.includes("agedQuantity")) {
      formattedDetails["agedQuantity"] = row.agedQuantity;
    }
    
    if (row.supplierDetails && typeof row.supplierDetails === "object") {
      const supplierDetails = extractSupplierDetails(row.supplierDetails);
      Object.entries(supplierDetails).forEach(([key, value]) => {
        if (!combinedExcludeFields.includes(key)) {
          formattedDetails[key] = value;
        }
      });
    }

    if (context === "itemAssignedRacks") {
      if (row.rackId && typeof row.rackId === "object" && !combinedExcludeFields.includes("rackName")) {
        const rackDetails = extractRackDetails(row.rackId);
        Object.entries(rackDetails).forEach(([key, value]) => {
          if (!combinedExcludeFields.includes(key)) {
            formattedDetails[key] = value;
          }
        });
      }

      if (row.itemId && typeof row.itemId === "object") {
        const partDetails = extractPartDetailsFromId(row.itemId);
        Object.entries(partDetails).forEach(([key, value]) => {
          if (!combinedExcludeFields.includes(key)) {
            formattedDetails[key] = value;
          }
        });
      } else if (row.partId && typeof row.partId === "object") {
        const partDetails = extractPartDetailsFromId(row.partId);
        Object.entries(partDetails).forEach(([key, value]) => {
          if (!combinedExcludeFields.includes(key)) {
            formattedDetails[key] = value;
          }
        });
      }

      if (row.status && !combinedExcludeFields.includes("status")) {
        formattedDetails["status"] = row.status;
      }

      return formattedDetails;
    }

    if (row.partId && typeof row.partId === "object") {
      const partDetails = extractPartDetailsFromId(row.partId);
      Object.entries(partDetails).forEach(([key, value]) => {
        if (!combinedExcludeFields.includes(key)) {
          formattedDetails[key] = value;
        }
      });
    }

    if (row.poId && typeof row.poId === "object") {
      const poDetails = extractPoDetailsFromId(row.poId);
      Object.entries(poDetails).forEach(([key, value]) => {
        if (!combinedExcludeFields.includes(key) && value && value !== "N/A") {
          formattedDetails[key] = value;
        }
      });
    }

    if (row.poNo && row.poNo.trim() !== "" && !combinedExcludeFields.includes("poNo")) {
      formattedDetails["poNo"] = row.poNo;
    }

    if (context === "priceHistory") {
      if (row.currentPrice && !combinedExcludeFields.includes("currentPrice")) {
        formattedDetails["Current Price"] = row.currentPrice;
      }
      
      if (row.previousPrice && !combinedExcludeFields.includes("previousPrice")) {
        formattedDetails["Previous Price"] = row.previousPrice;
      }
      
      if (row.internalPartNo && !combinedExcludeFields.includes("internalPartNo")) {
        formattedDetails["Internal Part No"] = row.internalPartNo;
      }
      
      if (row.itemmodel && typeof row.itemmodel === "object" && !combinedExcludeFields.includes("itemmodel")) {
        formattedDetails["Model"] = row.itemmodel.name || "N/A";
      }
      
      if (row.itemcategory && typeof row.itemcategory === "object" && !combinedExcludeFields.includes("itemcategory")) {
        formattedDetails["Category"] = row.itemcategory.name || "N/A";
      }
      
      if (row.uoms && typeof row.uoms === "object" && !combinedExcludeFields.includes("UOM")) {
        formattedDetails["UOM"] = row.uoms.name || "N/A";
        uomAdded = true;
      }
      
      if (row.supplierName && !combinedExcludeFields.includes("supplierName")) {
        formattedDetails["Supplier Name"] = row.supplierName;
      }
 combinedExcludeFields.push(
        "currentPrice", 
        "previousPrice", 
        "internalPartNo", 
        "uom", 
        "uoms", 
        "category", 
        "model", 
        "itemcategory", 
        "itemmodel"
      );
    }

    for (const key in rest) {
      if (rest.hasOwnProperty(key)) {
        if (combinedExcludeFields.includes(key)) {
          continue;
        }

        const value = rest[key];

        if (key === "supplierId" || key === "partId" || key === "poId") {
          continue;
        }

        if (key === "parts") {
          try {
            const partData = typeof value === "string" ? JSON.parse(value) : value;
            if (partData.internalPartNo) {
              formattedDetails["internalPartNo"] = partData.internalPartNo;
            }
          } catch (e) {
            console.error("Error parsing parts:", e);
          }
        } else if (key === "uoms") {
          try {
            const uomData = typeof value === "string" ? JSON.parse(value) : value;
            if (uomData.name && !uomAdded) {
              if (context === "priceHistory") {
                formattedDetails["UOM"] = uomData.name;
              } else {
                formattedDetails["uom"] = uomData.name;
              }
              uomAdded = true;
            }
          } catch (e) {
            console.error("Error parsing uoms:", e);
          }
        } else if (key === "models") {
          try {
            const modelData = typeof value === "string" ? JSON.parse(value) : value;
            if (modelData.name) {
              formattedDetails["model"] = modelData.name;
            }
          } catch (e) {
            console.error("Error parsing models:", e);
          }
        } else if (key === "partDetails") {
          const extractedDetails = extractPartDetails(value);

          Object.entries(extractedDetails).forEach(
            ([detailKey, detailValue]) => {
              if (!combinedExcludeFields.includes(detailKey)) {
                formattedDetails[detailKey] = detailValue;
              }
            }
          );
        } else if (key === "poDetails") {
          const extractedDetails = extractPoDetails(value);

          Object.entries(extractedDetails).forEach(
            ([detailKey, detailValue]) => {
              if (
                !combinedExcludeFields.includes(detailKey) &&
                detailValue &&
                detailValue !== "N/A"
              ) {
                formattedDetails[detailKey] = detailValue;
              }
            }
          );
        } else if (key === "supplierparts") {
          const extractedDetails = extractSupplierDetails(value);

          Object.entries(extractedDetails).forEach(
            ([detailKey, detailValue]) => {
              if (!combinedExcludeFields.includes(detailKey)) {
                formattedDetails[detailKey] = detailValue;
              }
            }
          );
        } else if (key === "itemId" && typeof value === "object") {
          const extractedDetails = extractPartDetailsFromId(value);

          Object.entries(extractedDetails).forEach(
            ([detailKey, detailValue]) => {
              if (!combinedExcludeFields.includes(detailKey)) {
                formattedDetails[detailKey] = detailValue;
              }
            }
          );
        } else if (key === "rackId" && typeof value === "object" && !combinedExcludeFields.includes("rackName")) {
          const extractedDetails = extractRackDetails(value);

          Object.entries(extractedDetails).forEach(
            ([detailKey, detailValue]) => {
              if (!combinedExcludeFields.includes(detailKey)) {
                formattedDetails[detailKey] = detailValue;
              }
            }
          );
        } else if (key === "itemmodel" && typeof value === "object") {
          if (!formattedDetails["Model"]) {
            formattedDetails["Model"] = value.name || "N/A";
          }
        } else if (key === "itemcategory" && typeof value === "object") {
          if (!formattedDetails["Category"]) {
            formattedDetails["Category"] = value.name || "N/A";
          }
        } else if (typeof value === "object" && value !== null) {
          const formattedValue = formatNestedObject(value);

          if (key === "model" || key === "category") {
            formattedDetails[key] = formattedValue.name || "N/A";
          } else if (key === "uom") {
            if (!uomAdded) {
              if (context === "priceHistory") {
                formattedDetails["UOM"] = formattedValue.name || "N/A";
              } else {
                formattedDetails[key] = formattedValue.name || "N/A";
              }
              uomAdded = true;
            }
          } else if (key === "purchaseCurrency" || key === "sellingCurrency") {
            formattedDetails[key] = formattedValue.name || value.name || "Unknown Currency";
          } else if (key === "stateId") {
            formattedDetails["stateName"] = extractStateDetails(value);
          } else if (key === "countryId") {
            formattedDetails["countryName"] = extractCountryDetails(value);
          } else {
            const stringifiedValue = JSON.stringify(formattedValue);
            if (
              !stringifiedValue.includes("_id") &&
              !stringifiedValue.includes("Id")
            ) {
              formattedDetails[key] = stringifiedValue;
            }
          }
        } else if (
          key === "purchaseCurrencyId" ||
          key === "sellingCurrencyId" ||
          key === "customerId"
        ) {
          continue;
        } else if (
          key === "productionItem" ||
          key === "sellingItem" ||
          key === "purchaseItem" ||
          key === "cityId"
        ) {
          if (key !== "cityId") {
            formattedDetails[key] = value ? "Yes" : "No";
          }
        } else {
          if (value !== undefined && value !== null && value !== "") {
            formattedDetails[key] = value;
          }
        }
      }
    }

    return formattedDetails;
  };

  const sanitizeKey = (key: string) => {
    if (key === "Model" || key === "Category" || key === "UOM" || 
        key === "Current Price" || key === "Previous Price" || 
        key === "Supplier Name" || key === "Internal Part No" || 
        key === "Part Name" || key === "PO Number" || key === "PO Date" || 
        key === "PO Quantity" || key === "Balance Quantity" || 
        key === "Received Quantity" || key === "Due Date") {
      return key;
    }
    
    const title = key
      .replace(/([A-Z])/g, " $1") 
      .replace(/_/g, " ") 
      .replace(/^./, (str) => str.toUpperCase()); 
    if (key === "poNo") return "PO No";
    if (key === "poDate") return "PO Date";
    if (key === "rackStatus") return "Rack Status";
    if (key === "internalPartNo") return "Internal Part No";
    if (key === "uom") return "UOM";
    if (key === "cityName") return "City Name";
    if (key === "stateName") return "State Name";
    if (key === "countryName") return "Country Name";

    return title;
  };

  const truncateValue = (value: any): string => {
    if (value === null || value === undefined) return "";

    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
      const date = new Date(value);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${day}-${month}-${year}`;
    }
    if (typeof value !== "string") value = String(value);

    if (value.length > 50) {
      return value.replace(/(.{50})/g, "$1\n");
    }
    return value;
  };

  const formattedDetails = formatRowDetails(rowData);
  const detailKeys = Object.keys(formattedDetails);
  const totalDetails = detailKeys.length;

  const midPoint = Math.ceil(totalDetails / 2);
  const leftColumnDetails = Object.fromEntries(
    detailKeys.slice(0, midPoint).map((key) => [key, formattedDetails[key]])
  );
  const rightColumnDetails = Object.fromEntries(
    detailKeys.slice(midPoint).map((key) => [key, formattedDetails[key]])
  );


  const displayName = getDisplayName();

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className={`modal-content ${totalDetails > 6 ? "large-modal" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 className="modal-title">{displayName}</h2>
          <button className="modal-close-icon" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="row-details-container">
          {totalDetails > 6 ? (
            <div className="two-column-modal-layout">
              <div className="modal-column">
                {Object.entries(leftColumnDetails).map(([key, value]) => (
                  <div key={key} className="row-detail">
                    <strong>{sanitizeKey(key)}</strong>
                    <span style={{ whiteSpace: "pre-wrap" }}>
                      {truncateValue(value)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="modal-column">
                {Object.entries(rightColumnDetails).map(([key, value]) => (
                  <div key={key} className="row-detail">
                    <strong>{sanitizeKey(key)}</strong>
                    <span style={{ whiteSpace: "pre-wrap" }}>
                      {truncateValue(value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="two-column-layout">
              {Object.entries(formattedDetails).map(([key, value]) => (
                <div key={key} className="row-detail">
                  <strong>{sanitizeKey(key)}</strong>
                  <span style={{ whiteSpace: "pre-wrap" }}>
                    {truncateValue(value)}
                    </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="close-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RowDetailsModal;